import { BookOpen, Globe, MapPin, Phone, ExternalLink, AlertOctagon, Heart, Shield, Flame, Stethoscope, Baby, Ear, Users } from "lucide-react";

const RECENT_EDITIONS = [
  { id: "fev-2026", label: "Février 2026", subtitle: "n° 543 · Le grand printemps citoyen", url: "https://www.istres.fr/index.php?id=444" },
  { id: "jan-2026", label: "Janvier 2026", subtitle: "n° 542 · Vœux & projets", url: "https://www.istres.fr/index.php?id=444" },
  { id: "dec-2025", label: "Décembre 2025", subtitle: "n° 541 · Marchés de Noël", url: "https://www.calameo.com/books/00329196404296a7f5e79" },
  { id: "nov-2025", label: "Novembre 2025", subtitle: "n° 540 · Patrimoine d'Istres", url: "https://www.istres.fr/index.php?id=444" },
  { id: "oct-2025", label: "Octobre 2025", subtitle: "n° 539 · Rentrée des associations", url: "https://www.istres.fr/index.php?id=444" },
  { id: "archives", label: "Toutes les archives", subtitle: "Depuis 1966 · service archives municipales", url: "https://www.istres.fr/index.php?id=1070" },
];

const EMERGENCY_NUMBERS = [
  { num: "15",  label: "SAMU", desc: "Urgence médicale", icon: Stethoscope, color: "bg-pink-500" },
  { num: "17",  label: "Police secours", desc: "Police nationale", icon: Shield, color: "bg-plum-700" },
  { num: "18",  label: "Pompiers", desc: "Incendie · secours", icon: Flame, color: "bg-sun-500 text-plum-900" },
  { num: "112", label: "Numéro européen", desc: "Toutes urgences en Europe", icon: AlertOctagon, color: "bg-mint-600" },
  { num: "114", label: "Urgence sourds", desc: "SMS / fax / appli", icon: Ear, color: "bg-lavande-500" },
  { num: "119", label: "Enfance en danger", desc: "Allô enfance maltraitée", icon: Baby, color: "bg-pink-400" },
  { num: "3919", label: "Violences femmes", desc: "Anonyme · gratuit · 24/24", icon: Heart, color: "bg-pink-600" },
  { num: "04 13 29 50 00", label: "Police municipale", desc: "Mairie d'Istres", icon: Users, color: "bg-plum-500" },
];

export default function CityResourcesSection() {
  return (
    <section id="ressources" className="bg-white border-t border-cream-200" data-testid="city-resources">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="phenix-eyebrow mb-3 inline-flex">Ressources d&apos;Istres</div>
          <h2 className="heading-display text-[1.8rem] sm:text-[2.4rem] text-plum-900 leading-[1.1]">
            Tout sur votre ville, en un coup d&apos;œil.
          </h2>
          <p className="mt-3 text-[15px] text-plum-700/70 max-w-xl mx-auto">
            Sites officiels, magazine municipal et numéros utiles, rassemblés pour les Istréens.
          </p>
        </div>

        {/* Sites officiels */}
        <div className="grid sm:grid-cols-2 gap-3 mb-10" data-testid="city-official-links">
          <a href="https://www.istres.fr" target="_blank" rel="noreferrer" className="phenix-card phenix-card-hover p-5 flex items-center gap-4 group" data-testid="link-mairie">
            <div className="w-12 h-12 rounded-2xl bg-plum-700 text-white grid place-items-center shrink-0 shadow-sm">
              <Globe size={20} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="phenix-pill bg-plum-100 text-plum-700 mb-1 text-[10px]">Site officiel</div>
              <div className="font-display font-bold text-base text-plum-900">Mairie d&apos;Istres</div>
              <div className="text-[12px] text-plum-700/70 truncate">istres.fr · démarches, actus, conseils municipaux</div>
            </div>
            <ExternalLink size={16} className="text-plum-400 group-hover:text-plum-700 group-hover:translate-x-0.5 transition-all shrink-0" />
          </a>

          <a href="https://www.istres-tourisme.com" target="_blank" rel="noreferrer" className="phenix-card phenix-card-hover p-5 flex items-center gap-4 group" data-testid="link-tourisme">
            <div className="w-12 h-12 rounded-2xl bg-sun-400 text-plum-900 grid place-items-center shrink-0 shadow-sm">
              <MapPin size={20} strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="phenix-pill bg-sun-100 text-sun-700 mb-1 text-[10px]">Tourisme</div>
              <div className="font-display font-bold text-base text-plum-900">Office de tourisme</div>
              <div className="text-[12px] text-plum-700/70 truncate">istres-tourisme.com · visites, balades, agenda culturel</div>
            </div>
            <ExternalLink size={16} className="text-plum-400 group-hover:text-plum-700 group-hover:translate-x-0.5 transition-all shrink-0" />
          </a>
        </div>

        {/* Istres Mag */}
        <div className="mb-10" data-testid="istres-mag-section">
          <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-pink-500 text-white grid place-items-center shrink-0 shadow-sm">
                <BookOpen size={19} strokeWidth={2.2} />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-plum-900 leading-tight">Istres Mag</h3>
                <p className="text-[12px] text-plum-700/65">Magazine municipal · paraît depuis 1966</p>
              </div>
            </div>
            <a href="https://www.istres.fr/index.php?id=444" target="_blank" rel="noreferrer" className="text-[12px] font-bold text-plum-700 hover:text-pink-500 inline-flex items-center gap-1.5" data-testid="istres-mag-all">
              Voir toutes les éditions <ExternalLink size={12} />
            </a>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {RECENT_EDITIONS.map((ed) => (
              <a
                key={ed.id}
                href={ed.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`istres-mag-${ed.id}`}
                className="phenix-card phenix-card-hover p-3 flex flex-col group"
              >
                <div className="aspect-[3/4] rounded-lg bg-gradient-to-br from-plum-600 via-plum-700 to-pink-500 mb-2 grid place-items-center text-white shadow-inner relative overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,213,0,0.25),transparent_60%)]" />
                  <div className="relative z-10 text-center px-2">
                    <BookOpen size={20} className="mx-auto mb-1.5 opacity-90" strokeWidth={1.8} />
                    <div className="text-[10px] uppercase tracking-widest opacity-80">Istres</div>
                    <div className="font-display font-extrabold text-sm leading-tight">Mag</div>
                  </div>
                </div>
                <div className="font-display font-bold text-[12px] text-plum-900 leading-tight">{ed.label}</div>
                <div className="text-[10px] text-plum-700/65 line-clamp-2 mt-0.5 leading-snug">{ed.subtitle}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-pink-500 group-hover:gap-1.5 transition-all">
                  Lire le PDF <ExternalLink size={9} />
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Numéros d'urgence */}
        <div data-testid="emergency-numbers-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl bg-pink-600 text-white grid place-items-center shrink-0 shadow-sm">
              <Phone size={19} strokeWidth={2.2} />
            </div>
            <div>
              <h3 className="font-display font-bold text-lg text-plum-900 leading-tight">Numéros d&apos;urgence</h3>
              <p className="text-[12px] text-plum-700/65">Toujours gratuits depuis un téléphone, fixe ou portable.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {EMERGENCY_NUMBERS.map((e) => (
              <a
                key={e.num}
                href={`tel:${e.num.replace(/\s/g, "")}`}
                data-testid={`emergency-${e.num.replace(/\s/g, "")}`}
                className="phenix-card phenix-card-hover p-3 flex items-center gap-2.5 group"
              >
                <div className={`w-10 h-10 rounded-xl ${e.color} ${e.color.includes("text-") ? "" : "text-white"} grid place-items-center shrink-0 shadow-sm`}>
                  <e.icon size={16} strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-extrabold text-base text-plum-900 tabular-nums leading-none">{e.num}</div>
                  <div className="text-[11px] font-semibold text-plum-700 leading-tight mt-0.5 truncate">{e.label}</div>
                  <div className="text-[10px] text-plum-700/60 leading-tight truncate">{e.desc}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
